
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Button,
  Dialog,
  Container,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Visibility from "@mui/icons-material/Visibility";
import Download from "@mui/icons-material/Download";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import { message } from "antd";
import ApiCallService from "../../../../Service/ApiCallService";
import { motion } from "framer-motion";
import Spinner from "../../../../components/common/Spinner";

export default function SingleEventViewPage() {
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState("");

  useEffect(() => {
    if (id) fetchEventById(id);
  }, [id]);

  async function fetchEventById(id) {
    try {
      setLoading(true);
      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/calendarbooking/${id}`
      );
      setEventData(response?.data);
    } catch (err) {
      message.error("Failed to load event");
    }
    setLoading(false);
  }

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const hasSelectedTime = (date) =>
    Boolean(
      date &&
      !(
        (typeof date === "string" && !date.includes("T")) ||
        (typeof date === "string" && /T00:00:00(?:\.000)?Z$/.test(date))
      )
    );

  const formatTime = (date) =>
    hasSelectedTime(date)
      ? new Date(date).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  const calculateDuration = (start, end) => {
    const diffMs = new Date(end) - new Date(start);

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes} mins`;
    if (minutes === 0) return `${hours} hrs`;

    return `${hours} hrs ${minutes} mins`;
  };

  const isSameEventDate = (start, end) =>
    start && end && formatDate(start) === formatDate(end);

  const formatEventSchedule = (start, end) => {
    if (!start) return "";

    const startDate = formatDate(start);
    const endDate = end ? formatDate(end) : "";
    const startTime = formatTime(start);
    const endTime = formatTime(end);

    if (!startTime && !endTime) {
      return end && !isSameEventDate(start, end)
        ? `${startDate} - ${endDate}`
        : startDate;
    }

    const dateLabel =
      end && !isSameEventDate(start, end)
        ? `${startDate} - ${endDate}`
        : startDate;
    const timeLabel = [startTime, endTime].filter(Boolean).join(" - ");
    const duration =
      startTime && endTime ? ` (${calculateDuration(start, end)})` : "";

    return `${dateLabel} | ${timeLabel}${duration}`;
  };

  const getExt = (url) => url.split(".").pop().toLowerCase();

  const handlePreview = (url) => {
    setPreviewUrl(url);
    setPreviewType(getExt(url));
    setPreviewOpen(true);
  };

  const downloadDocument = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop();
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      message.error("Download failed");
    }
  };

  const getPrettyName = (filename) =>
    filename?.substring(filename.lastIndexOf("-") + 1);

  const getObjectLabel = (value) => {
    if (!value) return "-";
    if (typeof value === "string") return value || "-";

    return (
      value.name ||
      value.eventType ||
      value.chapterName ||
      value.StateName ||
      value.stateName ||
      value.fullName ||
      value.email ||
      value._id ||
      "-"
    );
  };

  const getSubTypeLabel = (subType) => {
    if (!subType) return "-";
    if (typeof subType === "string") return subType;

    return subType?.programId?.name || subType?.name || subType?._id || "-";
  };

  const getFieldValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  const isMeaningfulValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  const getDisplayValue = (value, fallback = "") => {
    if (!isMeaningfulValue(value)) return fallback;
    if (typeof value === "string") return value;
    return value;
  };

  const detailItems = [
    { label: "Event Type", value: getDisplayValue(eventData?.calendarTypeId?.name || getObjectLabel(eventData?.calendarTypeId)) },
    { label: "Event Sub Type", value: getDisplayValue(eventData?.calendarSubTypeId?.programId?.name || eventData?.calendarSubTypeId?.name || getSubTypeLabel(eventData?.calendarSubTypeId)) },
    { label: "Organisation", value: getDisplayValue(eventData?.organisationType) },
    { label: "State", value: getDisplayValue(eventData?.stateId?.stateName || getObjectLabel(eventData?.stateId)) },
    { label: "Chapter", value: getDisplayValue(eventData?.chapterId?.chapterName || getObjectLabel(eventData?.chapterId)) },
    { label: "Meeting Type", value: getDisplayValue(eventData?.meetingType) },

  ].filter((item) => isMeaningfulValue(item.value));

  const metadataItems = [
    {
      label: "Created By",
      value: getDisplayValue(
        [eventData?.createdBy?.userFirstName, eventData?.createdBy?.userLastName]
          .filter(Boolean)
          .join(" ")
      ),
    },
    { label: "Created On", value: getDisplayValue(eventData?.createdAt ? formatDate(eventData.createdAt) : "") },
    { label: "Updated On", value: getDisplayValue(eventData?.updatedAt ? formatDate(eventData.updatedAt) : "") },
    { label: "Program", value: getDisplayValue(eventData?.programId?.name) },
    { label: "Basket", value: getDisplayValue(eventData?.basketId?.name) },
  ].filter((item) => isMeaningfulValue(item.value));

  const headerChips = [
    eventData?.meetingType,
    eventData?.calendarTypeId?.name || getObjectLabel(eventData?.calendarTypeId),
    eventData?.calendarSubTypeId?.programId?.name || eventData?.calendarSubTypeId?.name || getSubTypeLabel(eventData?.calendarSubTypeId),
  ].filter(Boolean);

  const showMapCard = eventData?.meetingType === "Offline" && eventData?.meetingLocation;
  const showMeetingLinkCard = eventData?.meetingType === "Online" && eventData?.hyperlink;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 }, minHeight: "100vh", backgroundColor: "#f5f7f6", fontFamily: "Inter, sans-serif" }}>
      <Spinner loading={loading} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.75fr 0.95fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box>
          <Card
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{
              borderRadius: 4,
              boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h3" fontWeight={800} sx={{ color: "#0f172a", lineHeight: 1.2, mb: 1.2 }}>
                    {eventData?.heading || "Event Details"}
                  </Typography>
                  {eventData?.subheading ? (
                    <Typography color="#475569" sx={{ lineHeight: 1.7, maxWidth: 720 }}>
                      {eventData?.subheading}
                    </Typography>
                  ) : null}
                </Box>
              </Box>



              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                  gap: 2,
                  p: 2,
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #f8fbfa 0%, #eef4f1 100%)",
                  border: "1px solid #e5ece9",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" , gap: 1.2 }}>
                  <CalendarTodayIcon sx={{ color: "#5A2891", fontSize: 20 }} />
                  <Typography color="#334155" fontWeight={600}>
                    {eventData?.startDate ? formatDate(eventData.startDate) : "-"}    
                  
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <AccessTimeIcon sx={{ color: "#5A2891", fontSize: 20 }} />
                  <Typography color="#334155" fontWeight={600}>
                    {eventData?.startDate && eventData?.endDate
                      ? `${formatTime(eventData.startDate)} - ${formatTime(eventData.endDate)}`
                      : "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Typography color="#64748b" fontWeight={600}>
                    Duration:
                  </Typography>
                  <Typography color="#0f172a" fontWeight={700}>
                    {eventData?.startDate && eventData?.endDate
                      ? calculateDuration(eventData.startDate, eventData.endDate)
                      : "-"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                {detailItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: "#fcfdfd",
                      border: "1px solid #eef2ef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        fontWeight: 700,
                        mb: 0.8,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: "#0f172a", fontWeight: 700, fontSize: "1rem", wordBreak: "break-word" }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Stack spacing={3}>
          <Card
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{ borderRadius: 4, border: "1px solid #e5e7eb", boxShadow: "0 16px 35px rgba(15, 23, 42, 0.06)", flexDirection:"row" }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a", mb: 1.5 }}>
                Meeting Information
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2 }}>
                <Chip
                  label={eventData?.meetingType || "-"}
                  sx={{ backgroundColor: "#f0fdf4", color: "#166534", fontWeight: 700 }}
                />
                {eventData?.meetingType === "Online" && eventData?.hyperlink ? (
                  <Button
                    variant="contained"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => window.open(eventData?.hyperlink, "_blank")}
                    sx={{ backgroundColor: "#064e3b", borderRadius: 2, textTransform: "none" }}
                  >
                    Open Meeting
                  </Button>
                ) : null}
              </Box>

              {eventData?.meetingType === "Offline" ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1}}>
                    <LocationOnIcon sx={{ color: "#5A2891", mt: 0.2 }} />
                    <Typography color="#334155" sx={{ lineHeight: 1.6 }}>
                      {eventData?.meetingAddress || "-"}
                    </Typography>
                  </Box>
              
                </>
              ) : null}

              {showMeetingLinkCard ? (
                <Typography color="#334155" sx={{ lineHeight: 1.6, wordBreak: "break-word" }}>
                  {eventData?.hyperlink}
                </Typography>
              ) : null}
            </CardContent>
          </Card>

          {showMapCard ? (
            <Card
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{ borderRadius: 4, border: "1px solid #e5e7eb", boxShadow: "0 16px 35px rgba(15, 23, 42, 0.06)", overflow: "hidden" }}
            >
              <Box sx={{ px: 2.5, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                <Typography fontWeight={800} color="#0f172a">
                  Location
                </Typography>
           
              </Box>

              <iframe
                width="100%"
                height="220"
                style={{ border: 0, display: "block" }}
                src={`https://www.google.com/maps?q=${encodeURIComponent(eventData?.meetingAddress || eventData?.meetingLocation)}&output=embed`}
                title="Event location map"
              />

            
            </Card>
          ) : null}

          {eventData?.documents?.length > 0 ? (
            <Card
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{ borderRadius: 4, border: "1px solid #e5e7eb", boxShadow: "0 16px 35px rgba(15, 23, 42, 0.06)" }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <DescriptionIcon sx={{ color: "#156d4b" }} />
                  <Typography variant="h6" fontWeight={800} color="#0f172a">
                    Documents
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {eventData.documents.map((doc, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        p: 1.25,
                        borderRadius: 2,
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Typography sx={{ color: "#0f172a", fontWeight: 600, wordBreak: "break-word" }}>
                        {getPrettyName(doc.split("/").pop())}
                      </Typography>
                      <Box>
                        <IconButton onClick={() => handlePreview(doc)} sx={{ color: "#064e3b" }}>
                          <Visibility />
                        </IconButton>
                        <IconButton onClick={() => downloadDocument(doc)} sx={{ color: "#064e3b" }}>
                          <Download />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </Stack>
      </Box>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <IconButton
          onClick={() => setPreviewOpen(false)}
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            height: "500px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {["jpg", "png", "jpeg"].includes(previewType) && (
            <img src={previewUrl} style={{ maxHeight: "100%" }} alt="Document preview" />
          )}

          {previewType === "pdf" && (
            <iframe src={previewUrl} width="100%" height="100%" title="PDF preview" />
          )}
        </Box>
      </Dialog>
    </Container>
  );
}
